import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText
} from '../../styles'

export default function MembersByGender ({
  data,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const formattedDate = selectedDate.toFormat ('MMM yyyy')
  const token = AuthService.getToken ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [loading, setLoading] = useState (true)
  const [isMounted, setMounted] = useState (false)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [showDetails, setShowDetails] = useState (false)
  const [membersByGender, setMembersByGender] = useState (null)

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) setPeriodFilter (periodFilterOptions[0])
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()

      const filters = `start_date=${startDate}&end_date=${endDate}`

      const { data: membersByGender } = await cutileiApi.get (
        `/admin/dashboard/members_by_gender?${filters}`, requestConfig
      )

      setMembersByGender ([
        ['Gênero', 'Masculino', 'Feminino', 'Não informado'],
        ...Object.keys (membersByGender).map (month => ([
          month,
          membersByGender[month]['Masculino'] ?? 0,
          membersByGender[month]['Feminino'] ?? 0,
          membersByGender[month]['Não informar'] ?? 0
        ]))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  if (!data || Object.keys (data).length === 0) {
    return null
  }

  const monthMembersByGender = [
    ['Gênero', 'Membros'],
    ['Masculino', data[formattedDate]?.['Masculino'] ?? 0],
    ['Feminino', data[formattedDate]?.['Feminino'] ?? 0],
    ['Não informado', data[formattedDate]?.['Não informar'] ?? 0]
  ]

  return (
    <InfoContainer>
      <SubTitle>Membros por gênero - {selectedDate.toFormat ('MMMM / yyyy')}</SubTitle>
      <PieChart
        data={monthMembersByGender}
        format='amount'
        options={{colors: ['#35A7FF', '#FF3939', '#FFC100']}}
      />
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Membros por gênero'
        onClose={toggleDetails}
      >
        <FilterContainer>
          <Select
            name='periodFilter'
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => setPeriodFilter (option)}
            containerStyles={{width: 250}}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <ColumnChart
            data={membersByGender}
            options={{
              isStacked: false,
              colors: ['#35A7FF', '#FF3939', '#FFC100'],
              chartArea: {
                top: 35, left: 110, width: '100%', height: '80%'
              },
              vAxis: {
                title: 'Membros por gênero',
                titleTextStyle: {
                  bold: true
                }
              }
            }}
          />
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
